import type { AppUser, Role, Store, UserRole } from './types.js';

/**
 * Role scoping (spec §2), enforced app-side in the data layer on every
 * query. SharePoint permissions are coarse — this is the working access
 * model, and direct-SharePoint access remains a documented trust boundary.
 */

/** Roles that always see every store. */
export const SEE_ALL_STORES: readonly Role[] = ['Auditor', 'Admin'];

/**
 * Every store, or a slice? Auditors and Admins see all; an Executive sees
 * all UNLESS their Users & Roles rows name districts or stores (owner
 * ruling 2026-10-19 — e.g. a regional executive, or the Phase 7 simulated
 * one); then only those. Shared with the server API's policy.
 */
export function seesAllStores(user: Pick<AppUser, 'role' | 'districts' | 'storeNumbers'>): boolean {
  if (SEE_ALL_STORES.includes(user.role)) return true;
  return user.role === 'Executive' && user.districts.length === 0 && user.storeNumbers.length === 0;
}

export function canSeeStore(user: AppUser, store: Store): boolean {
  if (seesAllStores(user)) return true;
  // Explicit per-store grant (by StoreNumber) applies to any scoped role.
  const storeNumber = store.storeNumber.trim();
  if (user.storeNumbers.some((n) => equalsIgnoreCase(n.trim(), storeNumber))) {
    return true;
  }
  if (user.role === 'DM' || user.role === 'Executive') {
    // Compare trimmed and case-insensitively so 'dc metro' matches 'DC Metro'.
    const storeDistrict = store.district.trim();
    return user.districts.some((d) => equalsIgnoreCase(d.trim(), storeDistrict));
  }
  // StoreTeam: shared account signs in as the store's manager UPN.
  return equalsIgnoreCase(store.managerEmail, user.upn);
}

/**
 * Most-privileged-first. When a sign-in matches several UserRoles rows
 * (direct UPN row plus one or more Entra group rows), the effective role is
 * the first of these that any matched row grants, and district/store scopes
 * are the union across the matched rows — an Executive's from its Executive
 * rows only, anyone else's from every row EXCEPT Executive rows.
 */
const ROLE_PRECEDENCE: readonly Role[] = [
  'Admin',
  'Auditor',
  'DM',
  'Executive',
  'StoreTeam',
];

export interface ResolvedAccess {
  role: Role;
  /** Display name from the direct user row, if one matched. */
  displayName: string;
  districts: string[];
  storeNumbers: string[];
}

export function resolveAccess(
  rows: readonly UserRole[],
  upn: string,
  groupIds: readonly string[],
): ResolvedAccess | null {
  const matched = rows.filter(
    (r) =>
      r.active &&
      (r.principalType === 'group'
        ? r.groupId !== '' &&
          groupIds.some((g) => equalsIgnoreCase(g, r.groupId))
        : r.upn !== '' && equalsIgnoreCase(r.upn, upn)),
  );
  if (matched.length === 0) return null;

  const role = ROLE_PRECEDENCE.find((p) => matched.some((m) => m.role === p))!;
  const direct = matched.find((m) => m.principalType === 'user');
  // An Executive is limited only by Executive rows, and any Executive row
  // with no districts or stores means every store — a limit on one group
  // row never narrows an unlimited one, and an unrelated (e.g. store
  // team) row never narrows an Executive. The other way round, an
  // Executive row's LIMIT never widens a DM/StoreTeam's scope: those
  // districts are read-only reach, not a grant to conduct or manage there.
  const scoping =
    role === 'Executive'
      ? matched.filter((m) => m.role === 'Executive')
      : matched.filter((m) => m.role !== 'Executive');
  const unlimited =
    role === 'Executive' && scoping.some((m) => m.districts.length === 0 && m.storeNumbers.length === 0);
  return {
    role,
    displayName: direct?.displayName ?? '',
    districts: unlimited ? [] : unique(scoping.flatMap((m) => m.districts)),
    storeNumbers: unlimited ? [] : unique(scoping.flatMap((m) => m.storeNumbers)),
  };
}

function unique(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const key = v.trim().toLowerCase();
    if (key && !seen.has(key)) {
      seen.add(key);
      out.push(v.trim());
    }
  }
  return out;
}

export function scopeStores(user: AppUser, stores: Store[]): Store[] {
  return stores.filter((s) => canSeeStore(user, s));
}

/**
 * Who sees OTHER people's performance — names and numbers — over the stores
 * they can see: every staff role (owner ruling 2026-10-28: "if a person has
 * access to the district, they have access to the performance of that
 * district and its people"; replaces 2026-10-25's "a DM sees only their own
 * row"). Store teams never. Fails closed on a missing/unknown role.
 */
export function seesPeoplePerformance(role: string | null | undefined): boolean {
  return role === 'DM' || role === 'Auditor' || role === 'Admin' || role === 'Executive';
}

/**
 * Who sees store OWNERS (owner ruling 2026-10-28: staff only — store teams
 * never; owners never sign in). The server mirrors it: the Owners list is
 * readable by Executives, DMs and Auditors (+ Admin). Fails closed.
 */
export function seesOwners(role: string | null | undefined): boolean {
  return role === 'DM' || role === 'Auditor' || role === 'Admin' || role === 'Executive';
}

export function canConductAudits(role: Role): boolean {
  return role === 'Auditor' || role === 'DM' || role === 'Admin';
}

/**
 * Score visibility during/at the end of conducting an audit (his ruling
 * 2026-08-10): nobody sees a live score while answering; Auditors and
 * Admins see the score on the pre-submit summary; DMs see no score until
 * after submission (it appears on the submitted audit record).
 */
export function canSeeScoreBeforeSubmit(role: Role): boolean {
  return role === 'Auditor' || role === 'Admin';
}

export function isAdmin(role: Role): boolean {
  return role === 'Admin';
}

export function equalsIgnoreCase(a: string, b: string): boolean {
  return a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;
}

/** Districts and store numbers round-trip as semicolon-delimited text columns. */
export function parseDelimited(raw: string | undefined | null): string[] {
  if (!raw) return [];
  return raw
    .split(';')
    .map((d) => d.trim())
    .filter((d) => d.length > 0);
}

export function serializeDelimited(values: string[]): string {
  return values
    .map((d) => d.trim())
    .filter((d) => d.length > 0)
    .join(';');
}
